import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ADDRESS_UI, type AddressDraft } from '../constants/addresses'
import { ORDER_PATHS } from '../constants/orders'
import { readAddressEditReturn } from '../helpers/addresses'
import { toE164Phone } from '../helpers/auth'
import { nationalPhoneDigits } from '../helpers/orders'
import { useSavedAddresses } from './useSavedAddresses'

export function useAddressEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = readAddressEditReturn(location.state)
  const { addresses, loading, save } = useSavedAddresses()
  const address = addresses.find((item) => item.id === id) ?? null
  const [draft, setDraft] = useState<AddressDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hydratedId = useRef<string | null>(null)
  const backLabel =
    returnTo === ORDER_PATHS.checkout ? ADDRESS_UI.backToCheckout : ADDRESS_UI.back

  useEffect(() => {
    if (!address || hydratedId.current === address.id) return
    hydratedId.current = address.id
    setDraft({
      recipientName: address.recipientName,
      recipientPhone: nationalPhoneDigits(address.recipientPhone),
      address: address.address,
      city: address.city,
      pincode: address.pincode ?? '',
      ...(typeof address.lat === 'number' ? { lat: address.lat } : {}),
      ...(typeof address.lng === 'number' ? { lng: address.lng } : {}),
    })
  }, [address])

  function updateDraft(patch: Partial<AddressDraft>) {
    setDraft((current) => (current ? { ...current, ...patch } : current))
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!id || !draft) return
    if (
      !draft.recipientName.trim() ||
      nationalPhoneDigits(draft.recipientPhone).length !== 10 ||
      !draft.address.trim() ||
      !draft.city.trim() ||
      !draft.pincode.trim()
    ) {
      setError(ADDRESS_UI.saveError)
      return
    }
    setSaving(true)
    setError(null)
    try {
      await save(
        {
          recipientName: draft.recipientName.trim(),
          recipientPhone: toE164Phone(draft.recipientPhone),
          address: draft.address.trim(),
          city: draft.city.trim(),
          pincode: draft.pincode.trim(),
          ...(typeof draft.lat === 'number' ? { lat: draft.lat } : {}),
          ...(typeof draft.lng === 'number' ? { lng: draft.lng } : {}),
        },
        id,
      )
      navigate(returnTo)
    } catch {
      setError(ADDRESS_UI.saveError)
      setSaving(false)
    }
  }

  return {
    loading,
    missing: !loading && !address,
    draft,
    updateDraft,
    saving,
    error,
    returnTo,
    backLabel,
    onSubmit,
  }
}
